<template>
  <v-container fluid class="customer-chat-page pa-0">
    <v-alert
      v-if="errorMessage"
      type="error"
      dense
      text
      class="mb-4"
    >
      {{ errorMessage }}
    </v-alert>

    <div class="chat-page-shell">
      <div class="chat-page-header">
        <div class="chat-page-title-wrap">
          <div class="chat-page-kicker">
            Customer service
          </div>

          <h1 class="chat-page-title">
            Customer Chat
          </h1>
          {{ authUser }}
        </div>

        <div class="chat-page-actions">
          <v-select
            v-model="sourceFilter"
            :items="sourceOptions"
            label="Source page"
            clearable
            outlined
            dense
            hide-details
            prepend-inner-icon="mdi-web"
            class="chat-source-select"
            @change="onSourceFilterChange"
          />

          <div class="chat-online-toggle">
            <span class="chat-online-label">
              {{ isOnline ? 'Online' : 'Offline' }}
            </span>

            <v-switch
              v-model="isOnline"
              :loading="statusLoading"
              inset
              color="success"
              hide-details
              class="ma-0 pa-0"
              @change="toggleOnlineStatus"
            />
          </div>
        </div>
      </div>

      <v-tabs
        v-model="activeTab"
        background-color="transparent"
        color="primary"
        class="chat-main-tabs"
      >
        <v-tab href="#live">
          Live Chat
        </v-tab>

        <v-tab href="#history">
          History / Sessions
        </v-tab>

        <v-tab  v-if="isSuperAdmin" href="#translator-usage">
          Translator Usage
        </v-tab>

        <v-tab href="#quick-replies">
          Template Messages
        </v-tab>
      </v-tabs>

      <v-tabs-items
        v-model="activeTab"
        class="chat-tabs-items"
      >
        <v-tab-item value="live">
          <div class="chat-summary-grid">
            <div class="chat-summary-card chat-summary-card-waiting">
              <div class="chat-summary-icon">
                <v-icon>mdi-account-clock-outline</v-icon>
              </div>
              <div>
                <div class="chat-summary-number">
                  {{ waitingSessions.length }}
                </div>
                <div class="chat-summary-label">
                  Waiting chats
                </div>
              </div>
            </div>

            <div class="chat-summary-card chat-summary-card-active">
              <div class="chat-summary-icon">
                <v-icon>mdi-chat-processing-outline</v-icon>
              </div>
              <div>
                <div class="chat-summary-number">
                  {{ activeSessions.length }}
                </div>
                <div class="chat-summary-label">
                  Active chats
                </div>
              </div>
            </div>

            <div class="chat-summary-card chat-summary-card-unread">
              <div class="chat-summary-icon">
                <v-icon>mdi-email-unread-outline</v-icon>
              </div>
              <div>
                <div class="chat-summary-number">
                  {{ totalUnreadCount }}
                </div>
                <div class="chat-summary-label">
                  Unread messages
                </div>
              </div>
            </div>

            <div class="chat-summary-card chat-summary-card-source">
              <div class="chat-summary-icon">
                <v-icon>mdi-web</v-icon>
              </div>
              <div>
                <div class="chat-summary-number">
                  {{ sourceOptions.length }}
                </div>
                <div class="chat-summary-label">
                  Source page
                </div>
              </div>
            </div>
          </div>

          <v-row class="chat-live-layout" no-gutters>
            <v-col cols="12" lg="4" xl="4" class="chat-list-column">
              <div class="chat-list-card">
                <div class="chat-list-header">
                  <div>
                    <h2>Waiting chats</h2>
                    <p>Customers waiting for an operator.</p>
                  </div>

                  <v-chip small color="orange lighten-5" text-color="orange darken-2" class="font-weight-bold">
                    {{ waitingSessions.length }}
                  </v-chip>
                </div>

                <div class="chat-list-body">
                  <button
                    v-for="session in waitingSessions"
                    :key="session.id"
                    type="button"
                    class="chat-session-row"
                    :class="{ 'is-selected': isSelectedSession(session) }"
                    @click="selectSession(session)"
                  >
                    <span class="chat-session-avatar chat-session-avatar-waiting">
                      {{ getCustomerInitials(session) }}
                    </span>

                    <span class="chat-session-main">
                      <span class="chat-session-title-line">
                        <strong>{{ getCustomerName(session) }}</strong>

                        <v-chip
                          v-if="session.unread_count > 0"
                          x-small
                          color="red"
                          text-color="white"
                          class="ml-1"
                        >
                          {{ session.unread_count }}
                        </v-chip>
                      </span>

                      <span class="chat-session-preview">
                        {{ getSessionPreview(session) }}
                      </span>

                      <span class="chat-session-meta-line">
                        <span>{{ getSessionTime(session) }}</span>
                        <span>{{ getSourceLabel(session) }}</span>
                        <span class="chat-locale-chip">{{ getLocaleLabel(session) }}</span>
                        <span class="chat-status-pill chat-status-waiting">Waiting</span>
                      </span>
                    </span>

                    <span class="chat-session-action">
                      <v-btn
                        x-small
                        depressed
                        color="success"
                        :loading="acceptingId === session.id"
                        @click.stop="acceptSession(session)"
                      >
                        Accept
                      </v-btn>
                    </span>
                  </button>

                  <div v-if="!waitingSessions.length" class="chat-list-empty">
                    No waiting chats.
                  </div>
                </div>
              </div>

              <div class="chat-list-card mt-4">
                <div class="chat-list-header">
                  <div>
                    <h2>Active chats</h2>
                    <p>Currently active conversations.</p>
                  </div>

                  <v-chip small color="green lighten-5" text-color="green darken-2" class="font-weight-bold">
                    {{ activeSessions.length }}
                  </v-chip>
                </div>

                <div class="chat-list-body">
                  <button
                    v-for="session in activeSessions"
                    :key="'active-' + session.id"
                    type="button"
                    class="chat-session-row"
                    :class="{ 'is-selected': isSelectedSession(session) }"
                    @click="openActiveSession(session)"
                  >
                    <span class="chat-session-avatar chat-session-avatar-active">
                      {{ getCustomerInitials(session) }}
                    </span>

                    <span class="chat-session-main">
                      <span class="chat-session-title-line">
                        <strong>{{ getCustomerName(session) }}</strong>

                        <v-chip
                          v-if="session.unread_count > 0"
                          x-small
                          color="red"
                          text-color="white"
                          class="ml-1"
                        >
                          {{ session.unread_count }}
                        </v-chip>
                      </span>

                      <span class="chat-session-preview">
                        {{ getSessionPreview(session) }}
                      </span>

                      <span class="chat-session-meta-line">
                        <span>{{ getSessionTime(session) }}</span>
                        <span>{{ getSourceLabel(session) }}</span>
                        <span class="chat-locale-chip">{{ getLocaleLabel(session) }}</span>
                        <span class="chat-status-pill chat-status-active">Active</span>
                      </span>
                    </span>
                  </button>

                  <div v-if="!activeSessions.length" class="chat-list-empty">
                    No active chats.
                  </div>
                </div>
              </div>
            </v-col>

            <v-col cols="12" lg="8" xl="8" class="chat-detail-column">
              <div class="chat-detail-card">
                <template v-if="selectedSession">
                  <div class="chat-detail-header">
                    <div class="chat-detail-user">
                      <div class="admin-chat-avatar chat-detail-avatar">
                        {{ getCustomerInitials(selectedSession) }}
                      </div>

                      <div class="chat-detail-user-text">
                        <div class="chat-detail-title-row">
                          <h2>{{ getCustomerName(selectedSession) }}</h2>

                          <v-chip
                            small
                            :color="getStatusColor(selectedSession.status)"
                            text-color="white"
                            class="font-weight-bold"
                          >
                            {{ getStatusLabel(selectedSession.status) }}
                          </v-chip>
                        </div>

                        <div class="chat-detail-meta-line">
                          <span>
                            <v-icon x-small>mdi-web</v-icon>
                            {{ getSourceLabel(selectedSession) }}
                          </span>
                          <span>
                            <v-icon x-small>mdi-translate</v-icon>
                            {{ getLocaleLabel(selectedSession) }}
                          </span>
                          <span>
                            <v-icon x-small>mdi-account-tie-outline</v-icon>
                            {{ selectedSession.operator_display_name || 'No operator assigned' }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <v-btn
                      v-if="selectedSession.status === 'waiting'"
                      color="success"
                      depressed
                      :loading="acceptingId === selectedSession.id"
                      @click="acceptSession(selectedSession)"
                    >
                      Accept
                    </v-btn>
                  </div>

                  <div class="chat-detail-meta-cards">
                    <div class="chat-detail-meta-card">
                      <span>Started</span>
                      <strong>{{ formatDate(selectedSession.started_at || selectedSession.created_at) }}</strong>
                    </div>

                    <div class="chat-detail-meta-card">
                      <span>Connected</span>
                      <strong>{{ selectedSession.accepted_at ? formatDate(selectedSession.accepted_at) : '-' }}</strong>
                    </div>

                    <div class="chat-detail-meta-card">
                      <span>Operator</span>
                      <strong>{{ selectedSession.operator_display_name || '-' }}</strong>
                    </div>
                  </div>

                  <v-row no-gutters class="chat-detail-content">
                    <v-col cols="12" xl="8" lg="8" class="chat-conversation-column">
                      <div class="admin-chat-messages" @click="markSelectedSessionRead">
                        <div
                          v-if="!selectedSession.messages || !selectedSession.messages.length"
                          class="admin-chat-empty"
                        >
                          <div class="admin-chat-empty-icon">
                            💬
                          </div>

                          <strong>No messages yet in this conversation.</strong>
                          <span>The customer's information is already visible, the first message will appear here.</span>
                        </div>

                        <div
                          v-for="message in selectedSession.messages"
                          :key="message.id"
                          class="admin-chat-message-row"
                          :class="{
                            'admin-chat-message-row-operator': message.sender_type === 'operator',
                            'admin-chat-message-row-system': message.sender_type === 'system'
                          }"
                        >
                          <div
                            v-if="message.sender_type !== 'operator'"
                            class="admin-chat-message-avatar"
                          >
                            {{ getMessageInitials(message) }}
                          </div>

                          <div
                            class="admin-chat-message-bubble"
                            :class="{
                              'admin-chat-message-bubble-customer': message.sender_type === 'customer',
                              'admin-chat-message-bubble-operator': message.sender_type === 'operator',
                              'admin-chat-message-bubble-system': message.sender_type === 'system'
                            }"
                          >
                            <div class="admin-chat-message-meta">
                              {{ getSenderLabel(message) }} · {{ formatDate(message.created_at) }}
                            </div>

                            <div class="admin-chat-message-text">
                              {{ message.display_message || message.message }}
                            </div>

                            <div
                              v-if="getReadLabel(message)"
                              class="admin-chat-read-status"
                              :class="{
                                'admin-chat-read-status-read': message.is_read,
                                'admin-chat-read-status-unread': !message.is_read,
                                'admin-chat-read-status-operator': message.sender_type === 'operator',
                                'admin-chat-read-status-customer': message.sender_type === 'customer'
                              }"
                            >
                              <span class="admin-chat-read-icon">
                                {{ message.is_read ? '✓✓' : '✓' }}
                              </span>
                              {{ getReadLabel(message) }}
                            </div>
                          </div>

                          <div
                            v-if="message.sender_type === 'operator'"
                            class="admin-chat-message-avatar admin-chat-message-avatar-operator"
                          >
                            {{ getMessageInitials(message) }}
                          </div>
                        </div>
                      </div>

                      <div
                        v-if="selectedSession.status === 'active'"
                        class="admin-chat-reply-box"
                        @click="markSelectedSessionRead"
                      >
                        <div
                          v-if="quickReplies.length"
                          class="admin-chat-quick-replies"
                        >
                          <div class="admin-chat-quick-replies-title">
                            Templates messages
                          </div>

                          <div class="admin-chat-quick-replies-list">
                            <button
                              v-for="reply in quickReplies"
                              :key="reply.id"
                              type="button"
                              class="admin-chat-quick-reply"
                              :class="{ 'is-selected': selectedQuickReplyId === reply.id }"
                              @click="applyQuickReply(reply)"
                            >
                              {{ reply.text }}
                            </button>
                          </div>
                        </div>

                        <v-textarea
                          v-model="replyMessage"
                          placeholder="Reply to the customer"
                          rows="3"
                          outlined
                          dense
                          hide-details
                          class="admin-chat-reply-input"
                          @input="onReplyMessageInput"
                        />

                        <div class="admin-chat-reply-actions">
                          <v-btn
                            color="error"
                            text
                            @click="closeSession"
                          >
                            CLOSE
                          </v-btn>

                          <v-btn
                            color="primary"
                            depressed
                            :loading="sending"
                            :disabled="!replyMessage"
                            @click="sendMessage"
                          >
                            SEND
                          </v-btn>
                        </div>
                      </div>

                      <v-alert
                        v-else
                        dense
                        text
                        type="info"
                        class="mt-4"
                      >
                        This conversation is currently not active, so you cannot reply.
                      </v-alert>
                    </v-col>

                    <v-col cols="12" xl="4" lg="4" class="chat-info-column">
                      <div class="chat-info-card">
                        <div class="chat-info-card-title">
                          Customer Information
                        </div>

                        <div class="chat-info-row">
                          <span>Name</span>
                          <strong>{{ getCustomerName(selectedSession) }}</strong>
                        </div>

                        <div class="chat-info-row">
                          <span>Email</span>
                          <strong>{{ selectedSession.customer_email || '-' }}</strong>
                        </div>

                        <div class="chat-info-row">
                          <span>Phone</span>
                          <strong>{{ selectedSession.customer_phone || '-' }}</strong>
                        </div>

                        <div class="chat-info-row">
                          <span>Language</span>
                          <strong>{{ getLocaleLabel(selectedSession) }}</strong>
                        </div>
                      </div>

                      <div class="chat-info-card mt-4">
                        <div class="chat-info-card-title">
                          Session Data
                        </div>

                        <div class="chat-info-row">
                          <span>Source Page</span>
                          <strong>{{ getSourceLabel(selectedSession) }}</strong>
                        </div>

                        <div class="chat-info-row chat-info-url">
                          <span>Page URL</span>
                          <strong>{{ selectedSession.page_url || '-' }}</strong>
                        </div>

                        <div class="chat-info-row">
                          <span>Status</span>
                          <strong>{{ getStatusLabel(selectedSession.status) }}</strong>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </template>

                <template v-else>
                  <div class="admin-chat-empty-panel">
                    <div class="admin-chat-empty-illustration">
                      <v-icon>mdi-chat-processing-outline</v-icon>
                    </div>

                    <h3>Select a conversation</h3>
                    <p>
                      Click on a waiting or active chat to continue the conversation.
                    </p>
                  </div>
                </template>
              </div>
            </v-col>
          </v-row>
        </v-tab-item>

        <v-tab-item value="history">
          <chat-history-list ref="chatHistoryList" :source-filter="sourceFilter" />
        </v-tab-item>

        <v-tab-item value="translator-usage">
          <div class="translator-usage-admin-card">
            <div class="translator-usage-admin-header">
              <div>
                <h2>Azure Translator usage</h2>
                <p>
                  Monthly character usage is calculated based on the `chat_translations.character_count` value stored in the ERP.
                  Translations returned from a template or cache are not counted as Azure usage.
                </p>
              </div>

              <v-btn
                small
                depressed
                color="primary"
                :loading="translatorUsageLoading"
                @click="fetchTranslatorUsage(true)"
              >
                <v-icon small left>mdi-refresh</v-icon>
                Update
              </v-btn>
            </div>

            <v-alert
              v-if="translatorUsageError"
              type="error"
              dense
              text
              class="mb-4"
            >
              {{ translatorUsageError }}
            </v-alert>

            <div v-if="translatorUsageLoading && !translatorUsage" class="translator-usage-loading">
              <v-progress-circular indeterminate color="primary" />
              <span>Load Usage Data...</span>
            </div>

            <template v-else-if="translatorUsage">
              <div class="translator-usage-progress-wrap">
                <div class="translator-usage-progress-head">
                  <div>
                    <span class="translator-usage-label">Aktuális hónap</span>
                    <strong>{{ translatorUsage.period_label }}</strong>
                  </div>
                  <div class="translator-usage-percent">
                    {{ formatPercent(translatorUsage.usage_percent) }}%
                  </div>
                </div>

                <v-progress-linear
                  :value="translatorUsageProgressValue"
                  :color="translatorUsageProgressColor"
                  height="12"
                  rounded
                />

                <div class="translator-usage-progress-foot">
                  <span>{{ formatNumber(translatorUsage.azure_used_characters) }} used character(s)</span>
                  <span>{{ formatNumber(translatorUsage.free_character_limit) }} characters / monthly limit</span>
                </div>
              </div>

              <div class="translator-usage-grid">
                <div class="translator-usage-stat">
                  <span class="translator-usage-stat-label">Azure usage</span>
                  <strong>{{ formatNumber(translatorUsage.azure_used_characters) }}</strong>
                  <small>estimated actual API characters</small>
                </div>

                <div class="translator-usage-stat">
                  <span class="translator-usage-stat-label">Remaining budget</span>
                  <strong>{{ formatNumber(translatorUsage.remaining_characters) }}</strong>
                  <small>F0 Free: {{ formatNumber(translatorUsage.free_character_limit) }} / month</small>
                </div>

                <div class="translator-usage-stat">
                  <span class="translator-usage-stat-label">Successful Azure Migration</span>
                  <strong>{{ formatNumber(translatorUsage.successful_translations) }}</strong>
                  <small>calculated without cache</small>
                </div>

                <div class="translator-usage-stat">
                  <span class="translator-usage-stat-label">Retrieved from the cache</span>
                  <strong>{{ formatNumber(translatorUsage.cached_characters) }}</strong>
                  <small>{{ formatNumber(translatorUsage.cached_translations) }} translation, no Azure costs</small>
                </div>

                <div class="translator-usage-stat">
                  <span class="translator-usage-stat-label">Static / Template</span>
                  <strong>{{ formatNumber(translatorUsage.static_characters) }}</strong>
                  <small>{{ formatNumber(translatorUsage.static_translations) }} message, no Azure costs</small>
                </div>

                <div class="translator-usage-stat translator-usage-stat-error">
                  <span class="translator-usage-stat-label">Failed</span>
                  <strong>{{ formatNumber(translatorUsage.failed_translations) }}</strong>
                  <small>{{ formatNumber(translatorUsage.failed_characters) }} character involved</small>
                </div>
              </div>

              <div class="translator-usage-section">
                <div class="translator-usage-section-title">
                  Daily Breakdown
                </div>

                <v-simple-table dense class="translator-usage-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Azure characters</th>
                      <th>All completed characters</th>
                      <th>Cached characters</th>
                      <th>Call Successful</th>
                      <th>Fail</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!translatorUsage.daily || !translatorUsage.daily.length">
                      <td colspan="6" class="text-center py-6 text--secondary">
                        There is no translation data for this month yet.
                      </td>
                    </tr>
                    <tr v-for="day in translatorUsage.daily" :key="day.date">
                      <td>{{ day.date }}</td>
                      <td>{{ formatNumber(day.azure_characters) }}</td>
                      <td>{{ formatNumber(day.completed_characters) }}</td>
                      <td>{{ formatNumber(day.cached_characters) }}</td>
                      <td>{{ formatNumber(day.successful_translations) }}</td>
                      <td>{{ formatNumber(day.failed_translations) }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>

              <div class="translator-usage-section">
                <div class="translator-usage-section-title">
                  Latest Translations
                </div>

                <v-simple-table dense class="translator-usage-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>To</th>
                      <th>Character</th>
                      <th>Status</th>
                      <th>Type</th>
                      <th>Original</th>
                      <th>Translation</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!translatorUsage.latest || !translatorUsage.latest.length">
                      <td colspan="7" class="text-center py-6 text--secondary">
                        There is no translation to display.
                      </td>
                    </tr>
                    <tr v-for="item in translatorUsage.latest" :key="item.id">
                      <td>{{ formatDate(item.created_at) }}</td>
                      <td>{{ getTranslationDirection(item) }}</td>
                      <td>{{ formatNumber(item.character_count) }}</td>
                      <td>
                        <v-chip x-small :color="getTranslationStatusColor(item.status)" dark>
                          {{ item.status }}
                        </v-chip>
                      </td>
                      <td>{{ getTranslationUsageType(item) }}</td>
                      <td class="translator-usage-preview">{{ item.original_preview }}</td>
                      <td class="translator-usage-preview">{{ item.translated_preview || item.error_message || '-' }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>

              <div class="translator-usage-updated">
                Utolsó frissítés: {{ formatDate(translatorUsage.updated_at) }}
              </div>
            </template>
          </div>
        </v-tab-item>

        <v-tab-item value="quick-replies">
          <div class="quick-reply-admin-card">
            <div class="quick-reply-admin-header">
              <div>
                <h2>Template Messages</h2>
                <p>
                  You can add pre-translated quick replies here.
                  In ERP, the Hungarian text appears, while the external chat uses RO, EN, or HU text depending on the page's language.
                </p>
              </div>

              <v-btn
                color="primary"
                depressed
                @click="openQuickReplyCreate"
              >
                <v-icon left>mdi-plus</v-icon>
                New template
              </v-btn>
            </div>

            <v-alert
              v-if="quickReplyAdminError"
              type="error"
              dense
              text
              class="mb-4"
            >
              {{ quickReplyAdminError }}
            </v-alert>

            <v-progress-linear
              v-if="quickReplyAdminLoading"
              indeterminate
              color="primary"
              class="mb-4"
            />

            <div v-if="!quickReplyAdminLoading && !quickReplyAdminItems.length" class="quick-reply-empty">
              <v-icon>mdi-message-text-outline</v-icon>
              <strong>No template message has been added yet.</strong>
              <span>Click the "New Template" button and enter the text in Hungarian, Romanian, and English.</span>
            </div>

            <v-simple-table v-else class="quick-reply-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>HU text</th>
                  <th>RO text</th>
                  <th>EN text</th>
                  <th>To</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th class="text-right">Action(s)</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="reply in quickReplyAdminItems"
                  :key="reply.id"
                >
                  <td>{{ reply.sort_order }}</td>
                  <td class="quick-reply-table-text">{{ getQuickReplyTranslation(reply, 'hu') }}</td>
                  <td class="quick-reply-table-text">{{ getQuickReplyTranslation(reply, 'ro') || '-' }}</td>
                  <td class="quick-reply-table-text">{{ getQuickReplyTranslation(reply, 'en') || '-' }}</td>
                  <td>
                    <v-chip x-small color="blue lighten-5" text-color="blue darken-2">
                      {{ getQuickReplyAudienceLabel(reply.audience) }}
                    </v-chip>
                  </td>
                  <td>{{ reply.category || '-' }}</td>
                  <td>
                    <v-chip
                      x-small
                      :color="reply.is_active ? 'green lighten-5' : 'grey lighten-3'"
                      :text-color="reply.is_active ? 'green darken-2' : 'grey darken-1'"
                    >
                      {{ reply.is_active ? 'Active' : 'Inactive' }}
                    </v-chip>
                  </td>
                  <td class="text-right">
                    <v-btn icon small @click="openQuickReplyEdit(reply)">
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      small
                      color="error"
                      :loading="quickReplyDeletingId === reply.id"
                      @click="deleteQuickReply(reply)"
                    >
                      <v-icon small>mdi-delete-outline</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <v-dialog v-model="quickReplyDialog" max-width="860px" persistent>
            <v-card class="quick-reply-dialog">
              <v-card-title class="quick-reply-dialog-title">
                {{ quickReplyFormMode === 'create' ? 'New template message' : 'Edit Template Message' }}
              </v-card-title>

              <v-card-text>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="quickReplyForm.key"
                      label="Key / ID"
                      hint="If left blank, it will be automatically generated from the Hungarian text."
                      persistent-hint
                      outlined
                      dense
                    />
                  </v-col>

                  <v-col cols="12" md="3">
                    <v-select
                      v-model="quickReplyForm.audience"
                      :items="quickReplyAudienceOptions"
                      label="Where should it appear?"
                      item-text="text"
                      item-value="value"
                      outlined
                      dense
                    />
                  </v-col>

                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model.number="quickReplyForm.sort_order"
                      type="number"
                      min="0"
                      label="Order"
                      outlined
                      dense
                    />
                  </v-col>

                  <v-col cols="12" md="8">
                    <v-text-field
                      v-model="quickReplyForm.category"
                      label="Category"
                      placeholder="e.g., Quote, Product, Shipping"
                      outlined
                      dense
                    />
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-switch
                      v-model="quickReplyForm.is_active"
                      label="Active"
                      color="success"
                      inset
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-textarea
                      v-model="quickReplyForm.translations.hu"
                      label="Hungarian text *"
                      rows="2"
                      outlined
                      dense
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-textarea
                      v-model="quickReplyForm.translations.ro"
                      label="Romanian text"
                      rows="3"
                      outlined
                      dense
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-textarea
                      v-model="quickReplyForm.translations.en"
                      label="English text"
                      rows="3"
                      outlined
                      dense
                    />
                  </v-col>
                </v-row>
              </v-card-text>

              <v-card-actions>
                <v-spacer />
                <v-btn text @click="closeQuickReplyDialog">
                  Cancel
                </v-btn>
                <v-btn
                  color="primary"
                  depressed
                  :loading="quickReplySaving"
                  :disabled="!quickReplyForm.translations.hu"
                  @click="saveQuickReply"
                >
                  Save
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-tab-item>
      </v-tabs-items>
    </div>
  </v-container>
</template>


<script>
import moment from "moment";
import ChatService from "@/api/chat.service";
import ChatHistoryList from "@/view/pages/chat/components/ChatHistoryList.vue";

export default {
  name: "ChatDashboard",
  components: { ChatHistoryList },

  data() {
    return {
      isOnline: false,
      statusLoading: false,
      waitingSessions: [],
      selectedSession: null,
      replyMessage: "",
      acceptingId: null,
      sending: false,
      errorMessage: null,
      pollingInterval: null,
      touchInterval: null,

      activeTab: "live",
      activeSessions: [],
      lastReadMarkAt: null,
      
      sourceFilter: null,
      sourceOptions: [],
      quickReplies: [],
      selectedQuickReplyId: null,
      selectedQuickReplyText: null,

      quickReplyAdminItems: [],
      quickReplyAdminLoading: false,
      quickReplyAdminLoaded: false,
      quickReplyAdminError: null,
      quickReplyDialog: false,
      quickReplyFormMode: "create",
      quickReplySaving: false,
      quickReplyDeletingId: null,
      quickReplyForm: this.getEmptyQuickReplyForm(),
      quickReplyAudienceOptions: [
        { text: "Admin + External Chat", value: "both" },
        { text: "Only admin", value: "admin" },
        { text: "Only external chat", value: "public" },
      ],

      translatorUsage: null,
      translatorUsageLoading: false,
      translatorUsageError: null,
      translatorUsageLoaded: false,
    };
  },

  mounted() {
    this.loadOperatorStatus();
    this.fetchSources();
    this.fetchQuickReplies();
    this.fetchWaitingSessions();
    this.fetchActiveSessions();
    this.startPolling();
  },

  beforeDestroy() {
    this.stopPolling();
  },

  watch: {
    activeTab(newValue) {
      if (newValue === 'history') {
        this.refreshHistoryTab();
      }

      if (newValue === 'translator-usage') {
        this.fetchTranslatorUsage();
      }

      if (newValue === 'quick-replies') {
        this.fetchQuickReplyAdminItems();
      }
    },
  },

  methods: {
    getEmptyQuickReplyForm() {
      return {
        id: null,
        key: "",
        audience: "both",
        category: "",
        is_active: true,
        sort_order: 0,
        translations: {
          hu: "",
          ro: "",
          en: "",
        },
      };
    },

    async fetchQuickReplyAdminItems(force = false) {
      if (this.quickReplyAdminLoading) {
        return;
      }

      if (this.quickReplyAdminLoaded && !force) {
        return;
      }

      if (!ChatService.getQuickRepliesManage) {
        this.quickReplyAdminError = "Hiányzik a ChatService.getQuickRepliesManage() metódus. Add hozzá az API service fájlhoz a ZIP notes mappájában lévő snippet alapján.";
        return;
      }

      this.quickReplyAdminLoading = true;
      this.quickReplyAdminError = null;

      try {
        const response = await ChatService.getQuickRepliesManage();
        this.quickReplyAdminItems = response.data.quick_replies || [];
        this.quickReplyAdminLoaded = true;
      } catch (error) {
        console.error("Unable to fetch chat quick reply manager list", error);
        this.quickReplyAdminError = "Nem sikerült lekérni a sablon üzeneteket.";
      } finally {
        this.quickReplyAdminLoading = false;
      }
    },

    openQuickReplyCreate() {
      this.quickReplyFormMode = "create";
      this.quickReplyForm = this.getEmptyQuickReplyForm();
      this.quickReplyDialog = true;
    },

    openQuickReplyEdit(reply) {
      this.quickReplyFormMode = "edit";
      this.quickReplyForm = {
        id: reply.id,
        key: reply.key || "",
        audience: reply.audience || "both",
        category: reply.category || "",
        is_active: !!reply.is_active,
        sort_order: reply.sort_order || 0,
        translations: {
          hu: this.getQuickReplyTranslation(reply, "hu"),
          ro: this.getQuickReplyTranslation(reply, "ro"),
          en: this.getQuickReplyTranslation(reply, "en"),
        },
      };
      this.quickReplyDialog = true;
    },

    closeQuickReplyDialog() {
      this.quickReplyDialog = false;
      this.quickReplySaving = false;
      this.quickReplyForm = this.getEmptyQuickReplyForm();
    },

    async saveQuickReply() {
      if (!this.quickReplyForm.translations.hu) {
        this.quickReplyAdminError = "A magyar szöveg megadása kötelező.";
        return;
      }

      if (!ChatService.createQuickReply || !ChatService.updateQuickReply) {
        this.quickReplyAdminError = "Hiányzik a createQuickReply / updateQuickReply API service metódus.";
        return;
      }

      this.quickReplySaving = true;
      this.quickReplyAdminError = null;

      const payload = {
        key: this.quickReplyForm.key || null,
        audience: this.quickReplyForm.audience,
        category: this.quickReplyForm.category || null,
        is_active: !!this.quickReplyForm.is_active,
        sort_order: parseInt(this.quickReplyForm.sort_order || 0, 10),
        translations: {
          hu: this.quickReplyForm.translations.hu || "",
          ro: this.quickReplyForm.translations.ro || "",
          en: this.quickReplyForm.translations.en || "",
        },
      };

      try {
        if (this.quickReplyFormMode === "edit" && this.quickReplyForm.id) {
          await ChatService.updateQuickReply(this.quickReplyForm.id, payload);
        } else {
          await ChatService.createQuickReply(payload);
        }

        this.closeQuickReplyDialog();
        this.quickReplyAdminLoaded = false;
        await this.fetchQuickReplyAdminItems(true);
        await this.fetchQuickReplies();
      } catch (error) {
        console.error("Unable to save chat quick reply", error);
        this.quickReplyAdminError = this.getApiErrorMessage(error, "Nem sikerült menteni a sablon üzenetet.");
      } finally {
        this.quickReplySaving = false;
      }
    },

    async deleteQuickReply(reply) {
      if (!reply || !reply.id) {
        return;
      }

      if (!ChatService.deleteQuickReply) {
        this.quickReplyAdminError = "Hiányzik a deleteQuickReply API service metódus.";
        return;
      }

      if (!window.confirm("Biztosan törlöd ezt a sablon üzenetet?")) {
        return;
      }

      this.quickReplyDeletingId = reply.id;
      this.quickReplyAdminError = null;

      try {
        await ChatService.deleteQuickReply(reply.id);
        this.quickReplyAdminLoaded = false;
        await this.fetchQuickReplyAdminItems(true);
        await this.fetchQuickReplies();
      } catch (error) {
        console.error("Unable to delete chat quick reply", error);
        this.quickReplyAdminError = "Nem sikerült törölni a sablon üzenetet.";
      } finally {
        this.quickReplyDeletingId = null;
      }
    },

    getQuickReplyTranslation(reply, locale) {
      if (!reply || !reply.translations) {
        return "";
      }

      return reply.translations[locale] || "";
    },

    getQuickReplyAudienceLabel(audience) {
      const labels = {
        both: "Admin + külső",
        admin: "Admin",
        public: "Külső chat",
      };

      return labels[audience] || audience;
    },

    getApiErrorMessage(error, fallback) {
      if (error && error.response && error.response.data) {
        if (error.response.data.message) {
          return error.response.data.message;
        }

        if (error.response.data.errors) {
          const firstKey = Object.keys(error.response.data.errors)[0];

          if (firstKey && error.response.data.errors[firstKey] && error.response.data.errors[firstKey][0]) {
            return error.response.data.errors[firstKey][0];
          }
        }
      }

      return fallback;
    },

    async fetchTranslatorUsage(force = false) {
      if (this.translatorUsageLoading) {
        return;
      }

      if (this.translatorUsageLoaded && !force) {
        return;
      }

      if (!ChatService.getTranslatorUsage) {
        this.translatorUsageError = "Hiányzik a ChatService.getTranslatorUsage() metódus. Add hozzá az API service fájlhoz a notes mappában lévő snippet alapján.";
        return;
      }

      this.translatorUsageLoading = true;
      this.translatorUsageError = null;

      try {
        const response = await ChatService.getTranslatorUsage();
        this.translatorUsage = response.data.usage || null;
        this.translatorUsageLoaded = true;
      } catch (error) {
        console.error("Unable to fetch translator usage", error);
        this.translatorUsageError = this.getApiErrorMessage(error, "Nem sikerült lekérni a fordító használatot.");
      } finally {
        this.translatorUsageLoading = false;
      }
    },

    formatNumber(value) {
      const number = Number(value || 0);

      return number.toLocaleString("hu-HU");
    },

    formatPercent(value) {
      const number = Number(value || 0);

      return number.toLocaleString("hu-HU", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },

    getTranslationDirection(item) {
      if (!item) {
        return "-";
      }

      const source = item.source_language || "?";
      const target = item.target_language || "?";

      return `${String(source).toUpperCase()} → ${String(target).toUpperCase()}`;
    },

    getTranslationStatusColor(status) {
      const colors = {
        completed: "success",
        pending: "warning",
        failed: "error",
      };

      return colors[status] || "grey";
    },

    getTranslationUsageType(item) {
      if (!item) {
        return "-";
      }

      if (item.from_cache) {
        return "Cache";
      }

      if (item.from_static) {
        return "Statikus / sablon";
      }

      if (item.status === "completed") {
        return "Azure API";
      }

      return "-";
    },

    async fetchQuickReplies() {
      if (!ChatService.getQuickReplies) {
        return;
      }

      try {
        const response = await ChatService.getQuickReplies({
          audience: "admin",
          locale: "hu",
        });

        this.quickReplies = response.data.quick_replies || [];
      } catch (error) {
        console.error("Unable to fetch chat quick replies", error);
      }
    },

    applyQuickReply(reply) {
      if (!reply || !reply.text) {
        return;
      }

      this.replyMessage = reply.text;
      this.selectedQuickReplyId = reply.id;
      this.selectedQuickReplyText = reply.text;
    },

    onReplyMessageInput(value) {
      if (!this.selectedQuickReplyId) {
        return;
      }

      if (value !== this.selectedQuickReplyText) {
        this.clearSelectedQuickReply();
      }
    },

    clearSelectedQuickReply() {
      this.selectedQuickReplyId = null;
      this.selectedQuickReplyText = null;
    },

    async loadOperatorStatus() {
      try {
        const response = await ChatService.getOperatorStatus();

        this.isOnline = !!response.data.status.is_online;

        if (this.isOnline) {
          this.startTouch();
        }
      } catch (error) {
        this.errorMessage = "Nem sikerült lekérni az operátor chat státuszt.";
      }
    },

    async toggleOnlineStatus(value) {
      this.statusLoading = true;
      this.errorMessage = null;
        
      try {
        if (value) {
          await ChatService.setOnline();
          this.isOnline = true;
          this.startTouch();
        } else {
          await ChatService.setOffline();
          this.isOnline = false;
          this.stopTouch();
        }
      } catch (error) {
        this.errorMessage = "Nem sikerült módosítani a chat státuszt.";
        await this.loadOperatorStatus();
      } finally {
        this.statusLoading = false;
      }
    },

    async fetchWaitingSessions() {
      try {
        const response = await ChatService.getWaitingSessions(this.getSourceParams());
        this.waitingSessions = response.data.sessions || [];
      } catch (error) {
        this.errorMessage = "Nem sikerült lekérni a várakozó chateket.";
      }
    },

    async fetchActiveSessions() {
      try {
        const response = await ChatService.getActiveSessions(this.getSourceParams());
        this.activeSessions = response.data.sessions || [];
      } catch (error) {
        this.errorMessage = "Nem sikerült lekérni az aktív chateket.";
      }
    },

    async acceptSession(session) {
      this.acceptingId = session.id;
      this.errorMessage = null;

      try {
        const response = await ChatService.acceptSession(session.id);
        this.selectedSession = response.data.session;
        this.replyMessage = "";
        this.clearSelectedQuickReply();
        await this.fetchWaitingSessions();
      } catch (error) {
        this.errorMessage = "Nem sikerült felvenni a beszélgetést.";
      } finally {
        this.acceptingId = null;
      }

      this.$nextTick(() => {
        this.scrollChatToBottom();
      });
    },

    selectSession(session) {
      this.selectedSession = session;
    },

    async sendMessage() {
      if (!this.replyMessage || !this.selectedSession) {
        return;
      }

      this.sending = true;

      try {
        const payload = {
          message: this.replyMessage,
        };

        if (this.selectedQuickReplyId) {
          payload.quick_reply_id = this.selectedQuickReplyId;
        }

        await ChatService.sendMessage(this.selectedSession.id, payload);

        this.replyMessage = "";
        this.clearSelectedQuickReply();
        await this.refreshSelectedSession();
      } catch (error) {
        this.errorMessage = "Nem sikerült elküldeni az üzenetet.";
      } finally {
        this.sending = false;
      }
    },

    async refreshSelectedSession() {
      if (!this.selectedSession) {
        return;
      }
    
      const response = await ChatService.getSession(this.selectedSession.id);
      this.selectedSession = response.data.session;
    
      this.$nextTick(() => {
        this.scrollChatToBottom();
      });
    },

    scrollChatToBottom() {
      const el = this.$el.querySelector(".admin-chat-messages");

      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    },

    async closeSession() {
      if (!this.selectedSession) {
        return;
      }

      try {
        await ChatService.closeSession(this.selectedSession.id);

        this.selectedSession = null;
        this.replyMessage = "";
        this.clearSelectedQuickReply();

        await this.fetchWaitingSessions();
        await this.fetchActiveSessions();
      } catch (error) {
        this.errorMessage = "Nem sikerült lezárni a beszélgetést.";
      }
    },

    startPolling() {
      this.pollingInterval = setInterval(() => {
        this.fetchWaitingSessions();
        this.fetchActiveSessions();

        if (this.selectedSession) {
          this.refreshSelectedSession();
        }
      }, 5000);
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }

      this.stopTouch();
    },

    startTouch() {
      this.stopTouch();

      this.touchInterval = setInterval(() => {
        ChatService.touch();
      }, 30000);
    },

    stopTouch() {
      if (this.touchInterval) {
        clearInterval(this.touchInterval);
        this.touchInterval = null;
      }
    },

    formatDate(value) {
      if (!value) {
        return "-";
      }

      return moment(value).format("YYYY.MM.DD. HH:mm");
    },

    getCustomerName(session) {
      if (!session) {
        return "Unknown customer";
      }
    
      return session.customer_name || "Unknown customer";
    },

    getCustomerContact(session) {
      if (!session) {
        return "-";
      }
    
      const parts = [];
    
      if (session.customer_email) {
        parts.push(session.customer_email);
      }
    
      if (session.customer_phone) {
        parts.push(session.customer_phone);
      }
    
      return parts.length ? parts.join(" · ") : "No contact information provided";
    },

    getCustomerInitials(session) {
      const name = this.getCustomerName(session);
    
      if (!name || name === "Unknown customer") {
        return "V";
      }
    
      const parts = name.trim().split(/\s+/);
    
      if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
      }
    
      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    },

    getSenderLabel(message) {
      if (message.sender_type === "customer") {
        return this.getCustomerName(this.selectedSession);
      }
    
      if (message.sender_type === "operator") {
        return this.selectedSession && this.selectedSession.operator_display_name
          ? this.selectedSession.operator_display_name
          : "Operator";
      }
    
      return "System";
    },

    getMessageInitials(message) {
      if (message.sender_type === "operator") {
        const operatorName = this.selectedSession
          ? this.selectedSession.operator_display_name
          : null;
      
        if (!operatorName) {
          return "O";
        }
      
        const parts = operatorName.trim().split(/\s+/);
      
        if (parts.length === 1) {
          return parts[0].charAt(0).toUpperCase();
        }
      
        return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
      }
    
      return this.getCustomerInitials(this.selectedSession);
    },

    getStatusLabel(status) {
      const labels = {
        waiting: "Waiting",
        active: "Active",
        offline_form: "Offline Message",
        missed: "Missed",
        closed: "Closed",
      };
    
      return labels[status] || status;
    },

    getStatusColor(status) {
      const colors = {
        waiting: "orange",
        active: "success",
        offline_form: "blue-grey",
        missed: "red",
        closed: "grey",
      };
    
      return colors[status] || "grey";
    },

    async openActiveSession(session) {
      try {
        const response = await ChatService.getSession(session.id);
        this.selectedSession = response.data.session;
        this.replyMessage = "";
        this.clearSelectedQuickReply();
      
        this.$nextTick(() => {
          this.scrollChatToBottom();
        });
      } catch (error) {
        this.errorMessage = "Failed to open the active chat.";
      }
    },

    async markSelectedSessionRead() {
      if (!this.selectedSession || !this.selectedSession.id) {
        return;
      }

      if (this.selectedSession.status !== "active") {
        return;
      }
    
      if (!this.selectedSession.messages || !this.selectedSession.messages.length) {
        return;
      }
    
      const hasUnreadCustomerMessage = this.selectedSession.messages.some((message) => {
        return message.sender_type === "customer" && !message.is_read;
      });
    
      if (!hasUnreadCustomerMessage) {
        return;
      }
    
      const now = Date.now();
    
      if (this.lastReadMarkAt && now - this.lastReadMarkAt < 1500) {
        return;
      }
    
      this.lastReadMarkAt = now;
    
      try {
        await ChatService.markSessionRead(this.selectedSession.id);
      
        this.selectedSession.messages = this.selectedSession.messages.map((message) => {
          if (message.sender_type === "customer") {
            return {
              ...message,
              is_read: true,
              read_at: new Date().toISOString(),
            };
          }
        
          return message;
        });
      
        await this.fetchWaitingSessions();
      
        if (this.fetchActiveSessions) {
          await this.fetchActiveSessions();
        }
      } catch (error) {
        console.error("Mark chat as read failed", error);
      }
    },

    getReadLabel(message) {
      if (!message) {
        return "";
      }
    
      if (message.sender_type === "operator") {
        return message.is_read ? "read by customer" : "sent to customer";
      }
    
      if (message.sender_type === "customer") {
        return message.is_read ? "read" : "new message";
      }
    
      return "";
    },

    formatTime(value) {
      if (!value) {
        return "-";
      }

      return moment(value).format("HH:mm");
    },

    getSessionTime(session) {
      if (!session) {
        return "-";
      }

      return this.formatTime(session.last_message_at || session.created_at);
    },

    getSessionPreview(session) {
      if (!session) {
        return "-";
      }

      if (session.last_message_preview) {
        return session.last_message_preview;
      }

      if (session.last_message && (session.last_message.display_message || session.last_message.message)) {
        return session.last_message.display_message || session.last_message.message;
      }

      if (session.messages && session.messages.length) {
        return session.messages[session.messages.length - 1].display_message || session.messages[session.messages.length - 1].message;
      }

      return "There is no message yet.";
    },

    getSourceLabel(session) {
      return session && session.source_site ? session.source_site : "-";
    },

    getLocaleLabel(session) {
      return session && session.locale ? String(session.locale).toUpperCase() : "-";
    },

    isSelectedSession(session) {
      return !!(this.selectedSession && session && parseInt(this.selectedSession.id, 10) === parseInt(session.id, 10));
    },

    async fetchSources() {
      try {
        const response = await ChatService.getSources();
        this.sourceOptions = response.data.sources || [];
      } catch (error) {
        this.errorMessage = "Failed to fetch source sites.";
      }
    },

    getSourceParams() {
      return {
        source_site: this.sourceFilter,
      };
    },

    onSourceFilterChange() {
      this.selectedSession = null;
      this.fetchWaitingSessions();
      this.fetchActiveSessions();
    
      window.dispatchEvent(new CustomEvent("chat-source-filter-change", {
        detail: {
          source_site: this.sourceFilter,
        },
      }));
    },

    refreshHistoryTab() {
      this.$nextTick(() => {
        if (
          this.$refs.chatHistoryList &&
          typeof this.$refs.chatHistoryList.refreshHistory === "function"
        ) {
          this.$refs.chatHistoryList.refreshHistory();
        }
      });
    },
  },

  computed: {
    translatorUsageProgressValue() {
      if (!this.translatorUsage) {
        return 0;
      }

      const percent = Number(this.translatorUsage.usage_percent || 0);

      return Math.min(100, Math.max(0, percent));
    },

    translatorUsageProgressColor() {
      const percent = this.translatorUsageProgressValue;

      if (percent >= 90) {
        return "error";
      }

      if (percent >= 70) {
        return "warning";
      }

      return "success";
    },

    totalUnreadCount() {
      const waiting = this.waitingSessions.reduce((sum, session) => {
        return sum + (session.unread_count || 0);
      }, 0);

      const active = this.activeSessions.reduce((sum, session) => {
        return sum + (session.unread_count || 0);
      }, 0);

      return waiting + active;
    },
      
    authUserGroups() {
      const user = this.$store.state.auth.user

      return user && user.user_groups ? user.user_groups : []
    },

    superAdminRole() {
      return this.authUserGroups.find(group =>
        group.translations.some(translation => translation.name === 'superadmin')
      )
    },

    isSuperAdmin() {
      return !!this.superAdminRole
    }
  },
};
</script>

<style scoped>
.customer-chat-page {
  background: #f4f7fb;
  min-height: calc(100vh - 96px);
}

.chat-page-shell {
  padding: 24px;
}

.chat-page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 12px;
}

.chat-page-kicker {
  margin-bottom: 4px;
  font-size: 12px;
  line-height: 1.2;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .08em;
  color: #64748b;
}

.chat-page-title {
  margin: 0;
  font-size: 30px;
  line-height: 1.15;
  font-weight: 900;
  color: #0f172a;
}

.chat-page-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 14px;
  flex-wrap: wrap;
}

.chat-source-select {
  width: 290px;
  max-width: 100%;
  background: #ffffff;
  border-radius: 14px;
}

.chat-online-toggle {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 40px;
  padding: 6px 12px 6px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #ffffff;
  box-shadow: 0 8px 18px rgba(15, 23, 42, .04);
}

.chat-online-label {
  font-size: 13px;
  font-weight: 800;
  color: #334155;
  white-space: nowrap;
}

.chat-main-tabs {
  margin-bottom: 18px;
  border-bottom: 1px solid #e2e8f0;
}

.chat-tabs-items {
  background: transparent !important;
}

.translator-usage-admin-card {
  padding: 22px;
  border: 1px solid #e2e8f0;
  border-radius: 22px;
  background: #ffffff;
  box-shadow: 0 18px 45px rgba(15, 23, 42, .06);
}

.translator-usage-admin-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
}

.translator-usage-admin-header h2 {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 900;
  color: #0f172a;
}

.translator-usage-admin-header p {
  max-width: 900px;
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: #64748b;
}

.translator-usage-loading {
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  color: #64748b;
  font-weight: 700;
}

.translator-usage-progress-wrap {
  padding: 18px;
  margin-bottom: 18px;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background: #f8fafc;
}

.translator-usage-progress-head,
.translator-usage-progress-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.translator-usage-progress-head {
  margin-bottom: 12px;
}

.translator-usage-progress-foot {
  margin-top: 10px;
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
}

.translator-usage-label {
  display: block;
  margin-bottom: 3px;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: .08em;
  color: #64748b;
}

.translator-usage-percent {
  font-size: 26px;
  line-height: 1;
  font-weight: 900;
  color: #0f172a;
}

.translator-usage-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 20px;
}

.translator-usage-stat {
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background: #ffffff;
}

.translator-usage-stat-label {
  display: block;
  margin-bottom: 8px;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: #64748b;
}

.translator-usage-stat strong {
  display: block;
  margin-bottom: 4px;
  font-size: 24px;
  line-height: 1.1;
  font-weight: 900;
  color: #0f172a;
}

.translator-usage-stat small {
  font-size: 12px;
  color: #64748b;
}

.translator-usage-stat-error strong {
  color: #dc2626;
}

.translator-usage-section {
  margin-top: 20px;
}

.translator-usage-section-title {
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 900;
  color: #0f172a;
}

.translator-usage-table {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
}

.translator-usage-table th {
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: #64748b !important;
  background: #f8fafc;
}

.translator-usage-preview {
  max-width: 260px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.translator-usage-updated {
  margin-top: 14px;
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  text-align: right;
}

@media (max-width: 1264px) {
  .translator-usage-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 760px) {
  .translator-usage-admin-header,
  .translator-usage-progress-head,
  .translator-usage-progress-foot {
    align-items: flex-start;
    flex-direction: column;
  }

  .translator-usage-grid {
    grid-template-columns: 1fr;
  }
}

.quick-reply-admin-card {
  padding: 22px;
  border: 1px solid #e2e8f0;
  border-radius: 22px;
  background: #ffffff;
  box-shadow: 0 18px 45px rgba(15, 23, 42, .06);
}

.quick-reply-admin-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
}

.quick-reply-admin-header h2 {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 900;
  color: #0f172a;
}

.quick-reply-admin-header p {
  max-width: 860px;
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: #64748b;
}

.quick-reply-empty {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px dashed #cbd5e1;
  border-radius: 18px;
  color: #64748b;
  text-align: center;
}

.quick-reply-empty .v-icon {
  font-size: 38px;
  color: #94a3b8;
}

.quick-reply-empty strong {
  color: #334155;
}

.quick-reply-table {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
}

.quick-reply-table th {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: #64748b !important;
  background: #f8fafc;
}

.quick-reply-table-text {
  max-width: 280px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.quick-reply-dialog-title {
  font-weight: 900;
  color: #0f172a;
}

.chat-summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(160px, 1fr));
  gap: 16px;
  margin-bottom: 18px;
}

.chat-summary-card {
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 86px;
  padding: 18px;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 12px 30px rgba(15, 23, 42, .055);
}

.chat-summary-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 46px;
  height: 46px;
  border-radius: 16px;
}

.chat-summary-icon .v-icon {
  font-size: 24px;
}

.chat-summary-card-waiting .chat-summary-icon {
  background: #fff7ed;
  color: #f97316;
}

.chat-summary-card-active .chat-summary-icon {
  background: #ecfdf5;
  color: #16a34a;
}

.chat-summary-card-unread .chat-summary-icon {
  background: #eff6ff;
  color: #2563eb;
}

.chat-summary-card-source .chat-summary-icon {
  background: #f1f5f9;
  color: #475569;
}

.chat-summary-number {
  font-size: 25px;
  line-height: 1;
  font-weight: 900;
  color: #0f172a;
}

.chat-summary-label {
  margin-top: 5px;
  font-size: 13px;
  line-height: 1.25;
  font-weight: 700;
  color: #475569;
}

.chat-live-layout {
  gap: 18px;
}

.chat-list-column {
  max-width: 520px;
  padding-right: 18px !important;
}

.chat-detail-column {
  flex: 1;
}

.chat-list-card,
.chat-detail-card,
.chat-info-card {
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 12px 32px rgba(15, 23, 42, .055);
  overflow: hidden;
}

.chat-list-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 18px 14px;
  border-bottom: 1px solid #eef2f7;
}

.chat-list-header h2 {
  margin: 0;
  font-size: 18px;
  line-height: 1.2;
  font-weight: 900;
  color: #0f172a;
}

.chat-list-header p {
  margin: 5px 0 0;
  font-size: 12px;
  color: #64748b;
}

.chat-list-body {
  padding: 8px;
}

.chat-session-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 10px;
  border: 0;
  border-radius: 16px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background .15s ease, box-shadow .15s ease, transform .15s ease;
}

.chat-session-row:hover,
.chat-session-row.is-selected {
  background: #eff6ff;
  box-shadow: inset 0 0 0 1px #dbeafe;
}

.chat-session-row:hover {
  transform: translateY(-1px);
}

.chat-session-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 900;
}

.chat-session-avatar-waiting {
  background: #fef3c7;
  color: #92400e;
}

.chat-session-avatar-active {
  background: #dbeafe;
  color: #1d4ed8;
}

.chat-session-main {
  min-width: 0;
  flex: 1 1 auto;
}

.chat-session-title-line {
  display: flex;
  align-items: center;
  min-width: 0;
}

.chat-session-title-line strong {
  overflow: hidden;
  color: #0f172a;
  font-size: 14px;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-session-preview {
  display: block;
  margin-top: 2px;
  overflow: hidden;
  color: #64748b;
  font-size: 12px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-session-meta-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 7px;
  color: #64748b;
  font-size: 11px;
  line-height: 1.2;
}

.chat-locale-chip,
.chat-status-pill {
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  padding: 3px 7px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 900;
}

.chat-locale-chip {
  background: #e0f2fe;
  color: #0369a1;
}

.chat-status-waiting {
  background: #ffedd5;
  color: #c2410c;
}

.chat-status-active {
  background: #dcfce7;
  color: #15803d;
}

.chat-session-action {
  flex: 0 0 auto;
}

.chat-list-empty {
  padding: 18px 12px;
  color: #94a3b8;
  font-size: 13px;
  text-align: center;
}

.chat-detail-card {
  min-height: 690px;
}

.chat-detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px;
  border-bottom: 1px solid #eef2f7;
  background: linear-gradient(135deg, #ffffff, #f8fafc);
}

.chat-detail-user {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.chat-detail-avatar {
  width: 52px;
  height: 52px;
  border-radius: 18px;
}

.chat-detail-user-text {
  min-width: 0;
}

.chat-detail-title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 9px;
}

.chat-detail-title-row h2 {
  margin: 0;
  color: #0f172a;
  font-size: 21px;
  line-height: 1.2;
  font-weight: 900;
}

.chat-detail-meta-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 7px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.4;
}

.chat-detail-meta-line span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.chat-detail-meta-cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #eef2f7;
}

.chat-detail-meta-card {
  padding: 11px 13px;
  border: 1px solid #e2e8f0;
  border-radius: 15px;
  background: #ffffff;
}

.chat-detail-meta-card span {
  display: block;
  margin-bottom: 3px;
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .04em;
}

.chat-detail-meta-card strong {
  display: block;
  color: #0f172a;
  font-size: 13px;
  line-height: 1.35;
}

.chat-detail-content {
  padding: 18px 20px 20px;
}

.chat-conversation-column {
  padding-right: 16px !important;
}

.chat-info-column {
  padding-left: 16px !important;
}

.admin-chat-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 48px;
  height: 48px;
  border-radius: 16px;
  background: #1976d2;
  color: #ffffff;
  font-size: 16px;
  font-weight: 900;
}

.admin-chat-messages {
  min-height: 390px;
  max-height: 520px;
  overflow-y: auto;
  padding: 18px;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background:
    radial-gradient(circle at top left, rgba(37, 99, 235, .06), transparent 34%),
    linear-gradient(180deg, #ffffff, #fbfdff);
}

.admin-chat-message-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  margin-bottom: 16px;
}

.admin-chat-message-row-operator {
  justify-content: flex-end;
}

.admin-chat-message-row-system {
  justify-content: center;
}

.admin-chat-message-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: 12px;
  background: #e2e8f0;
  color: #334155;
  font-size: 11px;
  font-weight: 900;
}

.admin-chat-message-avatar-operator {
  background: #dbeafe;
  color: #1d4ed8;
}

.admin-chat-message-bubble {
  max-width: 74%;
  padding: 12px 14px;
  border-radius: 18px;
  box-shadow: 0 10px 22px rgba(15, 23, 42, .055);
}

.admin-chat-message-bubble-customer {
  background: #f1f5f9;
  color: #0f172a;
  border-bottom-left-radius: 7px;
}

.admin-chat-message-bubble-operator {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  color: #ffffff;
  border-bottom-right-radius: 7px;
}

.admin-chat-message-bubble-system {
  max-width: 86%;
  border: 1px solid #fde68a;
  background: #fffbeb;
  color: #92400e;
  text-align: center;
  box-shadow: none;
}

.admin-chat-message-meta {
  margin-bottom: 5px;
  font-size: 11px;
  line-height: 1.35;
  opacity: .78;
  font-weight: 800;
}

.admin-chat-message-text {
  font-size: 14px;
  line-height: 1.52;
  white-space: pre-line;
  word-break: break-word;
}

.admin-chat-read-status {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 8px;
  font-size: 11px;
  line-height: 1.3;
  font-weight: 900;
}

.admin-chat-read-icon {
  font-size: 12px;
  letter-spacing: -2px;
}

.admin-chat-read-status-read,
.admin-chat-message-bubble-customer .admin-chat-read-status-read {
  color: #16a34a;
}

.admin-chat-read-status-unread,
.admin-chat-message-bubble-customer .admin-chat-read-status-unread {
  color: #dc2626;
}

.admin-chat-message-bubble-operator .admin-chat-read-status-read {
  color: #bbf7d0;
}

.admin-chat-message-bubble-operator .admin-chat-read-status-unread {
  color: #fde68a;
}

.admin-chat-reply-box {
  margin-top: 14px;
  padding: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background: #f8fafc;
}

.admin-chat-reply-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
}

.chat-info-card {
  padding: 18px;
}

.chat-info-card-title {
  margin-bottom: 14px;
  color: #0f172a;
  font-size: 16px;
  font-weight: 900;
}

.chat-info-row {
  display: grid;
  grid-template-columns: 92px minmax(0, 1fr);
  gap: 10px;
  padding: 9px 0;
  border-top: 1px solid #f1f5f9;
}

.chat-info-row:first-of-type {
  border-top: 0;
}

.chat-info-row span {
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
}

.chat-info-row strong {
  min-width: 0;
  color: #0f172a;
  font-size: 13px;
  line-height: 1.35;
  word-break: break-word;
}

.chat-info-url strong {
  word-break: break-all;
}

.admin-chat-empty,
.admin-chat-empty-panel {
  min-height: 260px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 7px;
  text-align: center;
  color: #64748b;
}

.admin-chat-empty-panel {
  min-height: 690px;
}

.admin-chat-empty-icon,
.admin-chat-empty-illustration {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 70px;
  height: 70px;
  margin-bottom: 8px;
  border-radius: 24px;
  background: #eff6ff;
  color: #2563eb;
  font-size: 30px;
}

.admin-chat-empty-illustration .v-icon {
  color: #2563eb;
  font-size: 38px;
}

.admin-chat-empty strong,
.admin-chat-empty-panel h3 {
  margin: 0;
  color: #0f172a;
  font-size: 22px;
  font-weight: 900;
}

.admin-chat-empty span,
.admin-chat-empty-panel p {
  margin: 0;
  max-width: 430px;
  font-size: 14px;
  line-height: 1.55;
}

@media (max-width: 1263px) {
  .chat-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .chat-list-column {
    max-width: none;
    padding-right: 0 !important;
    margin-bottom: 18px;
  }

  .chat-detail-column {
    width: 100%;
  }

  .chat-conversation-column,
  .chat-info-column {
    padding-right: 0 !important;
    padding-left: 0 !important;
  }

  .chat-info-column {
    margin-top: 16px;
  }
}

@media (max-width: 767px) {
  .chat-page-shell {
    padding: 16px;
  }

  .chat-page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .chat-page-actions {
    justify-content: flex-start;
  }

  .chat-source-select {
    width: 100%;
  }

  .chat-summary-grid {
    grid-template-columns: 1fr;
  }

  .chat-detail-header,
  .chat-detail-meta-cards,
  .chat-detail-content {
    padding-left: 14px;
    padding-right: 14px;
  }

  .chat-detail-meta-cards {
    grid-template-columns: 1fr;
  }

  .admin-chat-message-bubble {
    max-width: 84%;
  }

  .chat-session-action {
    display: none;
  }
}


.admin-chat-quick-replies {
  margin-bottom: 12px;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #f8fafc;
}

.admin-chat-quick-replies-title {
  margin-bottom: 8px;
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.admin-chat-quick-replies-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.admin-chat-quick-reply {
  padding: 7px 10px;
  border: 1px solid #dbeafe;
  border-radius: 999px;
  background: #ffffff;
  color: #1e3a8a;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  transition: all .15s ease;
}

.admin-chat-quick-reply:hover,
.admin-chat-quick-reply.is-selected {
  border-color: #3b82f6;
  background: #eff6ff;
}

</style>
